import type { ErpFunctionalGroup, PageRoute } from '@routes/pageRoutes.ts';
import type { PlanCapabilities } from '@app-types/site.types.ts';
import { evaluateRouteAccess } from '@features/cms/components/routeAccess.ts';

export interface ErpSectionDescriptor {
  group: ErpFunctionalGroup;
  routes: PageRoute[];
}

export type ErpUserProfile = 'admin' | 'reception' | 'professional' | 'sales' | 'default';

const ERP_GROUP_PROFILE_ALIASES: Record<string, ErpUserProfile> = {
  'erp:staff:admin': 'admin',
  admin: 'admin',
  administrator: 'admin',
  owner: 'admin',
  owners: 'admin',
  'staff:owner': 'admin',
  'staff:owners': 'admin',
  'erp:owner': 'admin',
  'erp:owners': 'admin',
  'staff:admin': 'admin',
  'erp:staff:reception': 'reception',
  recepcion: 'reception',
  recepción: 'reception',
  reception: 'reception',
  frontdesk: 'reception',
  'erp:staff:services': 'professional',
  services: 'professional',
  servicios: 'professional',
  support: 'professional',
  'erp:staff:sales': 'sales',
  sales: 'sales',
  vendedores: 'sales',
  ventas: 'sales',
};

// Orden de grupos por perfil de usuario
export const GROUP_ORDER_BY_PROFILE: Record<
  ErpUserProfile,
  readonly ErpFunctionalGroup[]
> = {
  // admin: ['finanzas', 'ventas', 'clientes', 'stock', 'compras', 'servicios', 'suscripciones', 'configuracion'],
  admin: [
    'ventas',
    'servicios',
    'rrhh',
    'finanzas',
    'clientes',
    'stock',
    'compras',
    'suscripciones',
    'configuracion',
  ],
  reception: [
    'servicios',
    'ventas',
    'clientes',
    'suscripciones',
    'finanzas',
    'stock',
    'compras',
    'rrhh',
    'configuracion',
  ],
  professional: [
    'servicios',
    'clientes',
    'ventas',
    'suscripciones',
    'finanzas',
    'stock',
    'compras',
    'rrhh',
    'configuracion',
  ],
  sales: [
    'ventas',
    'clientes',
    'servicios',
    'suscripciones',
    'finanzas',
    'stock',
    'compras',
    'rrhh',
    'configuracion',
  ],
  default: [
    'ventas',
    'clientes',
    'stock',
    'compras',
    'servicios',
    'suscripciones',
    'finanzas',
    'rrhh',
    'configuracion',
  ],
};

export const GROUP_ORDER: readonly ErpFunctionalGroup[] = GROUP_ORDER_BY_PROFILE.default;

export const GROUP_COPY: Record<ErpFunctionalGroup, { title: string; description: string }> = {
  overview: {
    title: 'Resumen',
    description: 'Accesos principales del ERP agrupados por módulo.',
  },
  ventas: {
    title: 'Ventas',
    description: 'Accedé a órdenes, carritos y flujos de punto de venta.',
  },
  clientes: {
    title: 'Clientes',
    description: 'Gestioná clientes, filtros, estadísticas y exportaciones.',
  },
  stock: {
    // title: 'Stock y almacenes',
    title: 'Stock',
    description: 'Controlá movimientos, ajustes y existencias por ubicación.',
  },
  compras: {
    title: 'Compras y proveedores',
    description: 'Gestioná órdenes de compra, recepciones y proveedores.',
  },
  servicios: {
    title: 'Servicios y turnos',
    description: 'Organizá agenda, asignaciones y seguimiento de turnos.',
  },
  suscripciones: {
    title: 'Suscripciones',
    description: 'Gestioná planes y suscripciones de clientes.',
  },
  rrhh: {
    title: 'RRHH',
    description: 'Administra staff, convenios y futuras liquidaciones.',
  },
  finanzas: {
    title: 'Finanzas',
    description: 'Aplicaciones dedicadas al manejo y seguimiento del dinero',
  },
  configuracion: {
    title: 'Configuración ERP',
    description: 'Parametrizá comisiones, roles y ajustes avanzados.',
  },
};

export const sortRoutes = (routes: PageRoute[]) =>
  routes
    .slice()
    .sort((a, b) => {
      const priorityA = a.priority ?? Number.POSITIVE_INFINITY;
      const priorityB = b.priority ?? Number.POSITIVE_INFINITY;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return a.title.localeCompare(b.title, 'es');
    });

export const getRouteAccent = (route: PageRoute, fallback: string) =>
  route.colorToken
    ? `var(--ant-color-${route.colorToken}, ${fallback})`
    : fallback;

export const getErpRoutes = (routes: readonly PageRoute[]): PageRoute[] =>
  routes.filter((route) => route.domain === 'erpDashboard' && route.group && route.group !== 'overview');

export interface ErpAccessContext {
  erpEnabled: boolean;
  isStaff: boolean;
  isSuperuser: boolean;
  hasErpPermission: boolean;
  permissionSet: ReadonlySet<string>;
  groups?: readonly string[];
  roles?: readonly string[];
  siteApps: readonly string[];
  features: Record<string, boolean | undefined>;
  planCapabilities?: PlanCapabilities;
}

interface BuildSectionsParams extends ErpAccessContext {
  routes: readonly PageRoute[];
  advancedMode?: boolean; // Si false, oculta rutas con advancedMode: true
  userProfile?: ErpUserProfile; // Perfil del usuario para ordenar grupos
  groups?: readonly string[]; // Grupos del usuario para derivar el perfil ERP
}

/**
 * Detecta el perfil del usuario basado en sus permisos
 */
const normaliseGroupToken = (value: string | undefined | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.toLowerCase() : null;
};

export const detectUserProfile = (
  permissionSet: ReadonlySet<string>,
  groups: readonly string[] = [],
): ErpUserProfile => {
  for (const group of groups) {
    const normalised = normaliseGroupToken(group);
    if (!normalised) continue;
    const fromGroup = ERP_GROUP_PROFILE_ALIASES[normalised];
    if (fromGroup) {
      return fromGroup;
    }
  }

  // Superusuarios o usuarios con permisos de configuración son Admin
  if (
    permissionSet.has('settings:write') ||
    permissionSet.has('cashflow:view') ||
    permissionSet.has('expenses:read')
  ) {
    return 'admin';
  }

  // Usuarios con permisos de appointments son Profesionales
  if (
    permissionSet.has('appointments:read') ||
    permissionSet.has('appointments:write') ||
    permissionSet.has('appointments:history:read')
  ) {
    return 'professional';
  }

  // Usuarios con permisos de órdenes son Ventas
  if (permissionSet.has('sale_orders:read') || permissionSet.has('sale_orders:write')) {
    return 'sales';
  }

  return 'default';
};

export const isErpRouteVisible = (route: PageRoute, ctx: ErpAccessContext): boolean => {
  // logInfo('route: ', route, 'ctx: ', ctx);

  if (!ctx.erpEnabled || !ctx.isStaff) {
    return false;
  }

  if (route.domain !== 'erpDashboard') {
    return false;
  }

  const isCrmRoute =
    route.featureFlag === 'crm' ||
    route.requiredApps?.some((app) => app === 'crm' || app.startsWith('crm__')) ||
    route.requiredAnyApps?.some((app) => app === 'crm' || app.startsWith('crm__'));

  if (!isCrmRoute && !ctx.hasErpPermission) {
    return false;
  }

  return evaluateRouteAccess(
    {
      key: route.key,
      featureFlag: route.featureFlag,
      requiredApps: route.requiredApps ?? [],
      requiredAnyApps: route.requiredAnyApps ?? [],
      requiredCapabilities: route.requiredCapabilities ?? [],
      requiredGroups: route.requiredGroups ?? [],
      requiredPermissions: route.requiredPermissions ?? [],
      requiresStaff: route.requiresStaff !== false,
      navGroup: route.navGroup,
      domain: route.domain,
      allowSuperuserWithoutApp: route.allowSuperuserWithoutApp,
    },
    {
      apps: ctx.siteApps,
      features: ctx.features,
      planCapabilities: ctx.planCapabilities,
      isStaff: ctx.isStaff,
      isSuperuser: ctx.isSuperuser,
      permissions: Array.from(ctx.permissionSet),
      groups: ctx.groups ?? [],
      roles: ctx.roles ?? [],
      requiresDomainPermission: route.navGroup === 'erp',
      domainPermissions: ctx.hasErpPermission || isCrmRoute ? Array.from(ctx.permissionSet) : [],
    },
  ).allowed;
};

export const buildErpSections = (params: BuildSectionsParams): ErpSectionDescriptor[] => {
  const { routes, advancedMode = true, userProfile, groups = [], ...access } = params;

  if (!access.erpEnabled || !access.isStaff) {
    return [];
  }

  // Detectar perfil del usuario si no se proporciona
  const profile = userProfile ?? detectUserProfile(access.permissionSet, groups);
  const groupOrder = GROUP_ORDER_BY_PROFILE[profile] ?? GROUP_ORDER;

  const grouped = new Map<ErpFunctionalGroup, PageRoute[]>();

  for (const route of routes) {
    if (!route.group || route.group === 'overview') {
      continue;
    }

    if (!isErpRouteVisible(route, { ...access, groups })) {
      continue;
    }

    // Filtrar por modo avanzado
    if (!advancedMode && route.advancedMode) {
      continue;
    }

    const current = grouped.get(route.group) ?? [];
    current.push(route);
    grouped.set(route.group, current);
  }

  return groupOrder
    .map((group) => {
      const groupRoutes = grouped.get(group);
      if (!groupRoutes || groupRoutes.length === 0) {
        return null;
      }

      return { group, routes: sortRoutes(groupRoutes) } satisfies ErpSectionDescriptor;
    })
    .filter((section): section is ErpSectionDescriptor => Boolean(section));
};
